import { useTranslation } from 'react-i18next';

import { IconAlertTriangle, IconClock, IconLock, IconRocket } from '@tabler/icons-react';

import { useNavigate } from '@tanstack/react-router';

import { ActionIcon, Indicator, Tooltip } from '@optiro/ui';

import { useAuth } from '@/providers/AuthProvider/AuthProvider';

// spec §6 "Banner / CTA" — collab/trial/past_due/locked each get an
// always-visible nudge (unlike TrialWelcomeModal, which is a one-time
// dismissible modal). Rendered as a compact topbar icon + hover tooltip
// rather than a full-width banner. Admin-gated: every target page here
// already requires administrator role at the policy layer.
export function CompanyStatusIndicator() {
  const { t } = useTranslation('billing');
  const { userData } = useAuth();
  const navigate = useNavigate();

  if (!userData || userData.role !== 'administrator') return null;

  const { status, trial_ends_at } = userData.company;

  const config = (() => {
    switch (status) {
      case 'collab':
        return {
          icon: <IconRocket size={18} />,
          color: 'violet',
          label: t('statusIndicator.collab'),
          to: '/app/projects' as const,
        };
      case 'trial': {
        const daysLeft = trial_ends_at
          ? Math.max(
              0,
              Math.ceil((new Date(trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
            )
          : null;
        return {
          icon: <IconClock size={18} />,
          color: 'blue',
          label:
            daysLeft !== null
              ? t('statusIndicator.trial', { count: daysLeft })
              : t('statusIndicator.trialUnknown'),
          to: '/app/billing' as const,
        };
      }
      case 'past_due':
        return {
          icon: <IconAlertTriangle size={18} />,
          color: 'orange',
          label: t('statusIndicator.pastDue'),
          to: '/app/billing' as const,
        };
      case 'locked':
        return {
          icon: <IconLock size={18} />,
          color: 'red',
          label: t('statusIndicator.locked'),
          to: '/app/billing' as const,
        };
      default:
        return null;
    }
  })();

  if (!config) return null;

  return (
    <Tooltip label={config.label} multiline w={240} withArrow position="bottom">
      <Indicator color={config.color} size={8} offset={3} processing={status === 'trial'}>
        <ActionIcon
          variant="light"
          color={config.color}
          size="lg"
          aria-label={config.label}
          onClick={() => navigate({ to: config.to })}
        >
          {config.icon}
        </ActionIcon>
      </Indicator>
    </Tooltip>
  );
}
